// LibraryPage.tsx — `/`: the scan library. A searchable card grid of the
// user's scans (pinned first, then newest), with pin, rename (Modal + Text),
// delete (ui.useConfirm — never a native dialog) and downloads per card, and
// a New Scan entry into /scan. Scans still refining poll every 5 s so their
// status badges settle without a reload. Tabs switch between the user's own
// scans and those shared with them; a user without the library capability
// (openscan.user) only has the shared list. Compute-capable users get a
// one-line summary of this month's compute usage (GET /api/usage).
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Spinner from 'react-bootstrap/Spinner';
import { BsPlusLg, BsSave } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { ui } from 'common';
import { api } from '../api';
import type { Scan, Usage } from '../api';
import { ScanCard } from '../components/ScanCard';
import { errorMessage } from '../format';

const { Text } = ui;

type View = 'mine' | 'shared';

// usageLine summarises a month of compute charges, '' when there are none.
export function usageLine(u: Usage): string {
  const kinds = Object.values(u.totals ?? {});
  const jobs = kinds.reduce((n, t) => n + t.jobs, 0);
  if (jobs === 0) return '';
  const minutes = kinds.reduce((n, t) => n + t.seconds, 0) / 60;
  const foreign = Object.values(u.foreign ?? {}).reduce((n, t) => n + t.jobs, 0);
  return (
    `Compute this month: ${jobs} job${jobs === 1 ? '' : 's'}, ${minutes.toFixed(minutes < 10 ? 1 : 0)} min` +
    (foreign ? ` (${foreign} on scans shared with you)` : '')
  );
}

export interface LibraryPageProps {
  // library: may own scans (openscan.user); without it only the shared list.
  library?: boolean;
  compute?: boolean;
}

export default function LibraryPage({ library = true, compute = false }: LibraryPageProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<View>(library ? 'mine' : 'shared');
  const [usage, setUsage] = useState('');
  const [scans, setScans] = useState<Scan[] | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState<Scan | null>(null);
  const [newName, setNewName] = useState('');
  const [confirm, confirmDialog] = ui.useConfirm();

  const effectiveView: View = library ? view : 'shared';
  const load = useCallback(
    async (q: string) => {
      try {
        const list = await api.listScans(q, effectiveView);
        console.debug(`[openscan/library] ${list.length} ${effectiveView} scans for q=${JSON.stringify(q)}`);
        setScans(list);
        setError('');
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [effectiveView],
  );

  useEffect(() => {
    if (!compute) return;
    let cancelled = false;
    api
      .usage()
      .then((u) => !cancelled && setUsage(usageLine(u)))
      .catch((err) => console.info('[openscan/library] usage unavailable:', errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [compute]);

  const switchView = (v: View) => {
    if (v === view) return;
    setScans(null);
    setView(v);
  };

  // Debounced search; the server filters name/notes/tags.
  useEffect(() => {
    const t = window.setTimeout(() => void load(query), 250);
    return () => window.clearTimeout(t);
  }, [query, load]);

  const anyRefining = !!scans?.some((s) => s.status === 'refining');
  useEffect(() => {
    if (!anyRefining) return;
    const t = window.setInterval(() => void load(query), 5000);
    return () => window.clearInterval(t);
  }, [anyRefining, query, load]);

  const sorted = useMemo(
    () => [...(scans ?? [])].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.created_at.localeCompare(a.created_at)),
    [scans],
  );

  const replace = (s: Scan) => setScans((prev) => prev?.map((x) => (x.id === s.id ? s : x)) ?? prev);

  const onPin = async (s: Scan) => {
    try {
      replace(await api.patchScan(s.id, { pinned: !s.pinned }));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const onDelete = async (s: Scan) => {
    const ok = await confirm({
      title: 'Delete Scan',
      body: `Delete "${s.name}" with its ${s.keyframe_count} keyframes and splats? This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteScan(s.id);
      console.info(`[openscan/library] deleted ${s.id}`);
      setScans((prev) => prev?.filter((x) => x.id !== s.id) ?? prev);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const saveRename = async () => {
    if (!renaming || !newName.trim()) return;
    try {
      replace(await api.patchScan(renaming.id, { name: newName.trim() }));
      setRenaming(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h1 className="h3 m-0 me-auto">Scans</h1>
        <Text placeholder="Filter by name, notes or tag" value={query} onChange={(e) => setQuery(e.target.value)} minWidth={24} aria-label="Filter scans" />
        {library && (
          <Button variant="primary" onClick={() => navigate('/scan')}>
            <BsPlusLg className="me-2" />
            New Scan
          </Button>
        )}
      </div>

      {library ? (
        <Nav variant="tabs" activeKey={view} onSelect={(k) => k && switchView(k as View)} className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="mine">My scans</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="shared">Shared with me</Nav.Link>
          </Nav.Item>
        </Nav>
      ) : (
        <p className="small text-body-secondary prose-column">
          Scans other people shared with you. Your account can open shared scans but not create its own; ask an authz admin for the openscan-user role to scan.
        </p>
      )}
      {usage && <p className="small text-body-secondary">{usage}</p>}

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {scans === null && !error && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {scans !== null && sorted.length === 0 && (
        <p className="text-body-secondary prose-column">
          {query
            ? 'No scans match that filter.'
            : effectiveView === 'shared'
              ? 'Nobody has shared a scan with you yet. Scans opened from a share link are not listed here.'
              : 'No scans yet. Print a sheet of markers, lay them around an object, and start a New Scan from your phone.'}
        </p>
      )}

      <div className="card-grid">
        {sorted.map((s) => (
          <ScanCard
            key={s.id}
            scan={s}
            onPin={onPin}
            onDelete={onDelete}
            onRename={(sc) => {
              setRenaming(sc);
              setNewName(sc.name);
            }}
          />
        ))}
      </div>

      <Modal show={!!renaming} onHide={() => setRenaming(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 m-0">Rename Scan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Text
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void saveRename()}
            style={{ width: '100%' }}
            aria-label="Scan name"
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={() => setRenaming(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void saveRename()} disabled={!newName.trim()}>
            <BsSave className="me-2" />
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      {confirmDialog}
    </>
  );
}
